import { useCallback, useMemo } from "react";

import filters from "../../../../filters";
import { useLibrary } from "../../../providers";
import { useFeature } from "../features";
import { useObjectMemo } from "../../../hooks";

import {
  INCOME_FIELD_DISPLAY_ORDER,
  INCOME_TYPE_TRANSLATION_DEFAULTS,
  INCOME_TYPE_TRANSLATION_KEYS,
} from "./constants";
import { incomeReportingForm } from "./form";
import { useCreateIncomeSource, useEditIncomeSource } from "./requests";

import type {
  CreateIncomeSourceOverrideRunProps,
  EditIncomeSourceOverrideRunProps,
  IncomeSourceField,
  IncomeTypeChoice,
} from "./types";

const INCOME_FIELD_EMPTY_DISPLAY = "-";

export const useIsIncomeReportingEnabled = () => {
  const { featureEnabled, features } = useFeature();

  const fieldsToDisplay = useMemo(
    () =>
      INCOME_FIELD_DISPLAY_ORDER.filter((field) => {
        if (field === "income" || field === "income_type") {
          return true;
        }
        return Object.keys(
          features.additional_income_reporting_fields || {},
        ).includes(field);
      }),
    [features],
  );

  return useObjectMemo({
    /**
     * Always includes income and income_type
     * Conditionally includes mortgage/rent, industry, occupation
     */
    fieldsToDisplay,
    isIncomeReportingEnabled: featureEnabled("olb_income_reporting"),
  });
};

export const useIncomeReportingFieldValues = () => {
  const l10n = useLibrary("translations");

  return useCallback(
    (
      field: IncomeSourceField,
      value: string | number | null | undefined,
      privacyOverride = true,
    ): string => {
      if (field === "income" || field === "mortgage_rent") {
        if (!value) {
          return INCOME_FIELD_EMPTY_DISPLAY;
        }

        return privacyOverride
          ? "******"
          : filters.currency(value as Cents, {
              hasDecimal: false,
              truncate: true,
            });
      }

      if (field === "income_type") {
        return l10n.getString(
          INCOME_TYPE_TRANSLATION_KEYS[value as IncomeTypeChoice],
          null,
          INCOME_TYPE_TRANSLATION_DEFAULTS[value as IncomeTypeChoice],
        );
      }

      return (value as string) || INCOME_FIELD_EMPTY_DISPLAY;
    },
    [l10n],
  );
};

export const useIncomeReportingForm = () => {
  const form = incomeReportingForm.useForm();
  const { submitForm, values } = form;
  const { createIncomeSource, loading: isCreateLoading } =
    useCreateIncomeSource();
  const { editIncomeSource, loading: isEditLoading } = useEditIncomeSource();

  const submitIncomeReport = useCallback(
    async (
      uuid?: string,
      overrideRunProps?:
        | EditIncomeSourceOverrideRunProps
        | CreateIncomeSourceOverrideRunProps,
    ) => {
      const { success } = await submitForm();
      if (success && uuid) {
        editIncomeSource(values, uuid, overrideRunProps);
      } else if (success) {
        createIncomeSource(values, overrideRunProps);
      }
    },
    [editIncomeSource, createIncomeSource, submitForm, values],
  );

  return useObjectMemo({
    form,
    submitIncomeReport,
    isLoading: isCreateLoading || isEditLoading,
  });
};
